import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';

interface BillingPeriod {
  id: string;
  name: string;
  description: string;
  days: number;
  status: 'active' | 'inactive';
  isDefault: boolean;
  createdAt: string;
}

// Mock data
const mockPeriods: BillingPeriod[] = [
  {
    id: '1',
    name: 'Mensal',
    description: 'Faturação mensal (30 dias)',
    days: 30,
    status: 'active',
    isDefault: true,
    createdAt: '2024-01-01',
  },
  {
    id: '2',
    name: 'Quinzenal',
    description: 'Faturação quinzenal (15 dias)',
    days: 15,
    status: 'active',
    isDefault: false,
    createdAt: '2024-01-01',
  },
  {
    id: '3',
    name: 'Semanal',
    description: 'Faturação semanal (7 dias)',
    days: 7,
    status: 'active',
    isDefault: false,
    createdAt: '2024-01-01',
  },
  {
    id: '4',
    name: 'Trimestral',
    description: 'Faturação trimestral (90 dias)',
    days: 90,
    status: 'active',
    isDefault: false,
    createdAt: '2024-01-15',
  },
  {
    id: '5',
    name: 'Semestral',
    description: 'Faturação semestral (180 dias)',
    days: 180,
    status: 'inactive',
    isDefault: false,
    createdAt: '2024-02-01',
  },
  {
    id: '6',
    name: 'Anual',
    description: 'Faturação anual (365 dias)',
    days: 365,
    status: 'active',
    isDefault: false,
    createdAt: '2024-01-10',
  },
];

const BillingPeriods: React.FC = () => {
  const [periods, setPeriods] = useState<BillingPeriod[]>(mockPeriods);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    days: number;
    status: 'active' | 'inactive';
    isDefault: boolean;
  }>({
    name: '',
    description: '',
    days: 30,
    status: 'active',
    isDefault: false,
  });

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip size="small" color="success" label="Activo" icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label="Inactivo" icon={<InactiveIcon />} />
    );
  };

  const stats = {
    total: periods.length,
    active: periods.filter(p => p.status === 'active').length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (period?: BillingPeriod) => {
    if (period) {
      setSelectedPeriod(period);
      setFormData({
        name: period.name,
        description: period.description,
        days: period.days,
        status: period.status,
        isDefault: period.isDefault,
      });
    } else {
      setSelectedPeriod(null);
      setFormData({
        name: '',
        description: '',
        days: 30,
        status: 'active',
        isDefault: false,
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedPeriod(null);
  };

  const handleSave = () => {
    if (selectedPeriod) {
      // If setting as default, remove default from others
      let updatedPeriods = periods;
      if (formData.isDefault && !selectedPeriod.isDefault) {
        updatedPeriods = periods.map(p => ({ ...p, isDefault: false }));
      }
      setPeriods(updatedPeriods.map(p =>
        p.id === selectedPeriod.id
          ? { ...p, ...formData }
          : p
      ));
    } else {
      // If setting as default, remove default from others
      let updatedPeriods = periods;
      if (formData.isDefault) {
        updatedPeriods = periods.map(p => ({ ...p, isDefault: false }));
      }
      const newPeriod: BillingPeriod = {
        id: crypto.randomUUID(),
        ...formData,
        createdAt: new Date().toISOString().split('T')[0],
      };
      setPeriods([...updatedPeriods, newPeriod]);
    }
    handleCloseEdit();
  };

  const handleDelete = (period: BillingPeriod) => {
    if (period.isDefault) {
      alert('Não pode eliminar o período predefinido. Por favor, defina outro período como predefinido primeiro.');
      return;
    }
    if (window.confirm(`Tem certeza que deseja eliminar o período "${period.name}"?`)) {
      setPeriods(periods.filter(p => p.id !== period.id));
    }
  };

  const handleSetDefault = (period: BillingPeriod) => {
    if (window.confirm(`Tem certeza que deseja definir "${period.name}" como período predefinido?`)) {
      setPeriods(periods.map(p => ({
        ...p,
        isDefault: p.id === period.id,
      })));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Períodos de Facturação
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          Novo Período
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 6 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Períodos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                  <Typography variant="caption" color="text.secondary">Activos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Periods Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell width={100} align="center">Dias</TableCell>
                <TableCell width={120}>Estado</TableCell>
                <TableCell width={150} align="center">Predefinido</TableCell>
                <TableCell width={120} align="center">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periods
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((period) => (
                <TableRow key={period.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {period.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {period.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={period.days} color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>{getStatusChip(period.status)}</TableCell>
                  <TableCell align="center">
                    {period.isDefault ? (
                      <Chip
                        size="small"
                        color="warning"
                        label="Predefinido"
                      />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleSetDefault(period)}
                        disabled={period.status === 'inactive'}
                      >
                        Definir
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenEdit(period)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(period)}
                      disabled={period.isDefault}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={periods.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon />
          {selectedPeriod ? 'Editar Período' : 'Novo Período'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Nome do Período"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Dias"
                value={formData.days}
                onChange={(e) => setFormData({ ...formData, days: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Predefinido"
                value={formData.isDefault ? 'yes' : 'no'}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.value === 'yes' })}
              >
                <MenuItem value="no">Não</MenuItem>
                <MenuItem value="yes">Sim</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || formData.days < 1}
          >
            {selectedPeriod ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingPeriods;
